import { useEffect, useRef, useState } from 'react';

/**
 * The `inert` attribute keeps the panel out of the tab order and the accessibility tree while it's
 * closed.
 *
 * The panel is always in the DOM ("closed" just translates it off-screen),
 * so without `inert` it stays focusable and parseable to screen-readers,
 * as well as reachable via keyboard navigation.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/inert
 *
 * Timing is asymmetric on purpose:
 * - opening clears `inert` immediately, so the panel is reachable as it slides in;
 * - closing waits out the slide-out transition, because marking an element inert
 *   mid-animation would drop it from the a11y tree while it's still visible.
 */
const useDeferredInert = (isOpen, transitionLengthMs) => {
    const panelRef = useRef(null);
    const [isInert, setIsInert] = useState(!isOpen);

    useEffect(() => {
        if (isOpen) {
            setIsInert(false);
            return undefined;
        }
        const timer = setTimeout(() => setIsInert(true), transitionLengthMs);
        return () => clearTimeout(timer);
    }, [isOpen, transitionLengthMs]);

    // `inert` is applied imperatively rather than as a JSX prop due to incompatibility
    // with Emotion. (Emotion will drops props it views as invalid via `@emotion/is-prop-valid`)
    //
    // TODO: This shouldn't be actual (fixed in `@emotion/is-prop-valid` v0.8.7, we are on v1.3.x transitively via `@emotion/styled`)
    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) {
            return;
        }
        if (isInert) {
            panel.setAttribute('inert', '');
        } else {
            panel.removeAttribute('inert');
        }
    }, [isInert]);

    return panelRef;
};

export default useDeferredInert;
